import { useEffect, useLayoutEffect } from "react";
import Lenis from "lenis";
import ChapterRail from "@/components/ChapterRail";
import Marquee from "@/components/Marquee";
import Noise from "@/scenes/Noise";
import Doorway from "@/scenes/Doorway";
import Intention from "@/scenes/Intention";
import Arriving from "@/scenes/Arriving";
import Encounter from "@/scenes/Encounter";
import Remains from "@/scenes/Remains";
import Sanctum from "@/scenes/Sanctum";
import Crossing from "@/scenes/Crossing";
import { AUTOPLAY_DURATION_MS, AUTOPLAY_IDLE_DELAY_MS, useIdleAutoplay } from "@/hooks/useIdleAutoplay";
import { BETA_URL } from "@/config/presentation";

const SCENE_IDS = [
  "scene-noise",
  "scene-doorway",
  "scene-intention",
  "scene-arriving",
  "scene-encounter",
  "scene-remains",
  "scene-sanctum",
  "scene-crossing",
];

const LENIS_OPTIONS = {
  lerp: 0.1,
  smoothWheel: true,
  syncTouch: false,
  wheelMultiplier: 0.85,
  touchMultiplier: 1,
  overscroll: false,
  autoRaf: true,
  autoResize: true,
  anchors: true,
};

const ACTIVITY_SELECTOR = ".th-scene, .th-marquee";

const useSmoothScroll = () => {
  useEffect(() => {
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let lenis = null;
    let layoutFrame = 0;
    let disposed = false;

    const refreshLayout = () => {
      cancelAnimationFrame(layoutFrame);
      layoutFrame = requestAnimationFrame(() => {
        lenis?.resize();
        window.dispatchEvent(new Event("threshold:layout-ready"));
        window.dispatchEvent(new Event("resize"));
      });
    };
    const start = () => {
      if (lenis || reducedMotion.matches) return;
      lenis = new Lenis(LENIS_OPTIONS);
      window.__lenis = lenis;
      refreshLayout();
    };
    const stop = () => {
      lenis?.destroy();
      lenis = null;
      window.__lenis = null;
    };
    const onMotionPreferenceChange = () => {
      if (reducedMotion.matches) stop();
      else start();
      refreshLayout();
    };

    start();
    if (document.readyState === "complete") refreshLayout();
    window.addEventListener("load", refreshLayout, { once: true });
    window.addEventListener("pageshow", refreshLayout);
    window.visualViewport?.addEventListener("resize", refreshLayout, { passive: true });
    if (reducedMotion.addEventListener) reducedMotion.addEventListener("change", onMotionPreferenceChange);
    else reducedMotion.addListener(onMotionPreferenceChange);
    document.fonts?.ready.then(() => {
      if (!disposed) refreshLayout();
    });

    return () => {
      disposed = true;
      cancelAnimationFrame(layoutFrame);
      window.removeEventListener("load", refreshLayout);
      window.removeEventListener("pageshow", refreshLayout);
      window.visualViewport?.removeEventListener("resize", refreshLayout);
      if (reducedMotion.removeEventListener) reducedMotion.removeEventListener("change", onMotionPreferenceChange);
      else reducedMotion.removeListener(onMotionPreferenceChange);
      stop();
    };
  }, []);
};

const usePresentationControls = () => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "ArrowRight" && e.key !== " " && e.key !== "ArrowLeft") return;
      e.preventDefault();
      const tops = SCENE_IDS.map((id) => document.getElementById(id)?.offsetTop ?? 0);
      const y = window.scrollY + 4;
      let idx = 0;
      tops.forEach((t, i) => {
        if (t <= y) idx = i;
      });
      idx = e.key === "ArrowLeft" ? Math.max(0, idx - 1) : Math.min(SCENE_IDS.length - 1, idx + 1);
      if (window.__lenis) window.__lenis.scrollTo(tops[idx], { duration: 1.6 });
      else {
        const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
        window.scrollTo({ top: tops[idx], behavior });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
};

const useSceneActivity = () => {
  useLayoutEffect(() => {
    const elements = document.querySelectorAll(ACTIVITY_SELECTOR);
    let observerReported = false;
    let fallbackTimer = 0;
    let layoutFrame = 0;

    const markVisibleScenes = () => {
      const margin = window.innerHeight * 0.25;
      elements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        element.classList.toggle("is-scene-active", rect.bottom >= -margin && rect.top <= window.innerHeight + margin);
      });
    };
    const scheduleRefresh = () => {
      cancelAnimationFrame(layoutFrame);
      layoutFrame = requestAnimationFrame(markVisibleScenes);
    };

    markVisibleScenes();
    if (typeof window.IntersectionObserver !== "function") {
      elements.forEach((element) => element.classList.add("is-scene-active"));
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        observerReported = true;
        entries.forEach((entry) => entry.target.classList.toggle("is-scene-active", entry.isIntersecting));
      },
      { rootMargin: "25% 0px", threshold: 0 }
    );
    elements.forEach((element) => observer.observe(element));
    fallbackTimer = window.setTimeout(() => {
      if (!observerReported) elements.forEach((element) => element.classList.add("is-scene-active"));
    }, 1200);
    window.addEventListener("pageshow", scheduleRefresh);
    window.addEventListener("threshold:layout-ready", scheduleRefresh);
    document.addEventListener("visibilitychange", scheduleRefresh);
    return () => {
      observer.disconnect();
      window.clearTimeout(fallbackTimer);
      cancelAnimationFrame(layoutFrame);
      window.removeEventListener("pageshow", scheduleRefresh);
      window.removeEventListener("threshold:layout-ready", scheduleRefresh);
      document.removeEventListener("visibilitychange", scheduleRefresh);
    };
  }, []);
};

export default function App() {
  useSmoothScroll();
  usePresentationControls();
  useSceneActivity();
  const autoplayActive = useIdleAutoplay();

  return (
    <main className="th-root" data-testid="threshold-presentation">
      <a
        className="th-wordmark"
        href={BETA_URL}
        target="_blank"
        rel="noreferrer"
        data-testid="wordmark-link"
      >
        <span>THRESHOLD</span>
        <i />
        <span className="th-wordmark-sub">A BETA PRESENTATION</span>
      </a>
      <ChapterRail />
      <div
        className={`th-autoplay-status ${autoplayActive ? "is-playing" : ""}`}
        data-testid="autoplay-status"
        data-duration-seconds={AUTOPLAY_DURATION_MS / 1000}
        data-idle-delay-seconds={AUTOPLAY_IDLE_DELAY_MS / 1000}
        aria-live="polite"
        aria-label={autoplayActive ? "Autoplay active" : "Autoplay paused"}
      >
        <i />
        <span>AUTO</span>
      </div>
      <Noise />
      <Doorway />
      <Intention />
      <Arriving />
      <Encounter />
      <Remains />
      <Marquee />
      <Sanctum />
      <Crossing />
      <div className="th-grain" aria-hidden="true" />
    </main>
  );
}
